import {Draft, PayloadAction, current} from '@reduxjs/toolkit';
import {AvailabilityApiResponse} from '../../../server/routes/availability/types';
import {ApiAvailabilityAction, AvailabilityState} from './availability/slice';
import {ANY_EMPLOYEE} from './constants';

export const dateSelect = (
  state: Draft<AvailabilityState>,
  action: {payload: {date: string} & TimeSelectAction},
) => {
  state.selected.date = action.payload.date;
  state.selected.startTime = action.payload.startTime;
  state.selected.hasDiscount = action.payload.hasDiscount;
  state.selected.totalPrice = action.payload.totalPrice;
  state.selected.originalPrice = action.payload.originalPrice;
};
export const employeeSelect = (
  state: Draft<AvailabilityState>,
  action: {payload: number | typeof ANY_EMPLOYEE},
) => {
  state.selected.employee = action.payload;
};

export const employeeMultiSelect = (
  state: Draft<AvailabilityState>,
  action: {
    payload: {menuItemId: string; employeeId?: number};
  },
) => {
  if (action.payload.employeeId === undefined) {
    delete state.selected.employeeMultiSelect[action.payload.menuItemId];
  } else {
    state.selected.employeeMultiSelect = {
      ...state.selected.employeeMultiSelect,
      [action.payload.menuItemId]: action.payload.employeeId,
    };
  }
};

export type TimeSelectAction = {
  startTime: string | null;
  hasDiscount: boolean;
  totalPrice: string;
  originalPrice?: string;
};

export const startTimeSelect = (
  state: Draft<AvailabilityState>,
  action: {
    payload: TimeSelectAction;
  },
) => {
  state.selected.startTime = action.payload.startTime;
  state.selected.hasDiscount = action.payload.hasDiscount;
  state.selected.totalPrice = action.payload.totalPrice;
  state.selected.originalPrice = action.payload.originalPrice;
};

export const apiResponse = (
  state: Draft<AvailabilityState>,
  action: PayloadAction<
    AvailabilityApiResponse,
    string,
    {
      arg: ApiAvailabilityAction;
      requestId: string;
      requestStatus: 'fulfilled';
    },
    never
  >,
) => {
  const {payload, meta} = action;
  state.data.moreDaysInTheFuture = payload.moreDaysInTheFuture;
  state.data.moreDaysInThePast = payload.moreDaysInThePast;
  state.data.lastIncludedDate = payload.lastIncludedDate;
  state.data.firstIncludedDate = payload.firstIncludedDate;
  state.data.multiStaffSelectionExperimentEnabled =
    payload.multiStaffSelectionExperimentEnabled;
  state.data.services = payload.services;
  state.data.skus = payload.skus;
  state.data.employees = {
    ...current(state.data.employees),
    ...payload.employees,
  };
  state.data.appointmentStartDate = payload.appointmentStartDate;
  state.data.employeeSelectionAllowed = payload.employeeSelectionAllowed;

  // If there is a startDate in the action payload then we are trying
  // to paginate into the future so just append the new days.
  // Otherwise we're dealing with a fresh request (remove sku, back/forward, app init)
  if (meta.arg.startDate) {
    Object.assign(state.data.days, payload.days);
    state.paginationFutureStatusRequest = 'fulfilled';
  } else {
    const days = Object.values(action.payload.days);
    state.data.days = payload.days;
    if (Object.keys(days).length === 0) {
      state.availabilityStatusRequest = 'fulfilled';
      return;
    }
    const appointmentDate = state.data.appointmentStartDate;

    // Default selection: day matching originalAppointmentDate if defined,
    // otherwise use first available day.
    const defaultSelectedDay = appointmentDate
      ? (days.find((day) => day.date === appointmentDate) ?? days[0])
      : days[0];

    if (!defaultSelectedDay) {
      state.availabilityStatusRequest = 'rejected';
      return;
    }

    // Keep track of the first date that is selected when the app
    // initialises. This is so that if an item is deleted or back/forward
    // we can reset the app back to the initial date when the user landed on
    // the page. This solves having to allow pagination into the past but
    // also still permits URL sharing to an exact calendar slot
    if (!state.selected.initialDate) {
      state.selected.initialDate = defaultSelectedDay.date;
    }

    state.selected.date = defaultSelectedDay.date;
  }

  state.availabilityStatusRequest = 'fulfilled';
};
